import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { RefreshCw, Users, Download, LogOut, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import './Admin.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counter, setCounter] = useState({ total: 150, registered: 0, remaining: 150 });
  const navigate = useNavigate();

  // Check authentication
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      toast.error('Veuillez vous connecter');
      navigate('/login');
    }
  }, [navigate]);

  const fetchRegistrations = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        navigate('/login');
        return;
      }

      const [regResponse, counterResponse] = await Promise.all([
        axios.get(`${API}/pre-registrations`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/counter`)
      ]);
      
      setRegistrations(regResponse.data.registrations);
      setCounter(counterResponse.data);
    } catch (error) {
      console.error('Error fetching registrations:', error);
      if (error.response?.status === 401) {
        toast.error('Session expirée, veuillez vous reconnecter');
        localStorage.removeItem('admin_token');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRegistrations();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    toast.success('Déconnexion réussie');
    navigate('/login');
  };

  const handleDelete = async (email) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'inscription de ${email} ?\n\nCette action est irréversible et mettra à jour le compteur de places.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/pre-registrations/${encodeURIComponent(email)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Inscription supprimée avec succès');
      
      // Refresh data
      fetchRegistrations();
    } catch (error) {
      console.error('Error deleting registration:', error);
      if (error.response?.status === 401) {
        toast.error('Session expirée, veuillez vous reconnecter');
        localStorage.removeItem('admin_token');
        navigate('/login');
      } else {
        toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
      }
    }
  };

  const exportToCSV = () => {
    if (registrations.length === 0) return;

    const headers = [
      'Prénom',
      'Nom',
      'Email',
      'Téléphone',
      'Société',
      'SIRET',
      'Catégories',
      'Sous-catégories',
      'Date d\'inscription'
    ];

    const csvData = registrations.map(reg => [
      reg.firstName,
      reg.lastName,
      reg.email,
      reg.phone,
      reg.companyName || '',
      reg.siret || '',
      reg.selectedCategories.join('; '),
      reg.selectedSubcategories.join('; '),
      new Date(reg.createdAt).toLocaleString('fr-FR')
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `zotpro_inscriptions_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  return (
    <div className="admin-page">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-content">
          <div className="header-left">
            <img 
              src="https://customer-assets.emergentagent.com/job_pro-service-hub-5/artifacts/w0r3tvio_APP%20ICON.png" 
              alt="ZotPro Logo" 
              className="admin-logo"
            />
            <h1 className="admin-title">Administration ZotPro</h1>
          </div>
          <Button onClick={handleLogout} className="logout-btn">
            <LogOut size={20} />
            Déconnexion
          </Button>
        </div>
      </header>

      <div className="admin-container">
        {/* Stats Cards */}
        <div className="stats-grid">
          <Card className="stat-card">
            <div className="stat-icon total">
              <Users size={32} />
            </div>
            <div className="stat-content">
              <p className="stat-label">Total Places</p>
              <p className="stat-value">{counter.total}</p>
            </div>
          </Card>
          <Card className="stat-card">
            <div className="stat-icon registered">
              <Users size={32} />
            </div>
            <div className="stat-content">
              <p className="stat-label">Inscrits</p>
              <p className="stat-value">{counter.registered}</p>
            </div>
          </Card>
          <Card className="stat-card">
            <div className="stat-icon remaining">
              <Users size={32} />
            </div>
            <div className="stat-content">
              <p className="stat-label">Places Restantes</p>
              <p className="stat-value">{counter.remaining}</p>
            </div>
          </Card>
        </div>

        {/* Actions */}
        <div className="admin-actions">
          <Button onClick={fetchRegistrations} className="action-btn">
            <RefreshCw size={20} />
            Actualiser
          </Button>
          <Button onClick={exportToCSV} className="action-btn export-btn">
            <Download size={20} />
            Exporter CSV
          </Button>
        </div>

        {/* Registrations Table */}
        <Card className="table-card">
          <div className="table-header">
            <h2>Liste des Pré-inscriptions ({registrations.length})</h2>
          </div>
          
          {loading ? (
            <div className="loading">Chargement...</div>
          ) : registrations.length === 0 ? (
            <div className="empty-state">Aucune pré-inscription pour le moment</div>
          ) : (
            <div className="table-container">
              <table className="registrations-table">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Email</th>
                    <th>Téléphone</th>
                    <th>Société</th>
                    <th>SIRET</th>
                    <th>Catégories</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {registrations.map((reg, index) => (
                    <tr key={index}>
                      <td className="name-cell">
                        {reg.firstName} {reg.lastName}
                      </td>
                      <td>{reg.email}</td>
                      <td>{reg.phone}</td>
                      <td>{reg.companyName || '-'}</td>
                      <td>{reg.siret || '-'}</td>
                      <td className="categories-cell">
                        <div className="categories-list">
                          {reg.selectedCategories.map((cat, idx) => (
                            <span key={idx} className="category-badge">{cat}</span>
                          ))}
                        </div>
                        {reg.selectedSubcategories && reg.selectedSubcategories.length > 0 && (
                          <div className="subcategories-list-admin">
                            {reg.selectedSubcategories.map((sub, idx) => (
                              <span key={idx} className="subcategory-badge">{sub}</span>
                            ))}
                          </div>
                        )}
                        {reg.autreCategory && (
                          <div className="autre-category-admin">
                            <span className="autre-badge">Autre : {reg.autreCategory}</span>
                          </div>
                        )}
                      </td>
                      <td className="date-cell">
                        {new Date(reg.createdAt).toLocaleString('fr-FR', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="actions-cell">
                        <Button 
                          onClick={() => handleDelete(reg.email)} 
                          className="delete-btn"
                          title="Supprimer cette inscription"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Admin;
